import React, { createContext, useContext, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import { useSiteContent, ContentType } from '@/hooks/useSiteContent'
import { useAuth } from '@/hooks/useAuth'

interface SiteEditContextValue {
  /** Recupere la valeur d'un bloc editable (avec fallback du contenu d'origine) */
  get: (key: string, fallback?: string) => string
  /** Enregistre une modification (admin uniquement) */
  save: (
    key: string,
    value: string,
    meta?: { type?: ContentType; section?: string; label?: string }
  ) => Promise<void>
  /** Mode edition actif (barre flottante) */
  editMode: boolean
  setEditMode: (v: boolean) => void
  /** L'utilisateur courant peut-il editer ? */
  canEdit: boolean
  /** Une sauvegarde est-elle en cours ? */
  saving: boolean
  loading: boolean
}

const SiteEditContext = createContext<SiteEditContextValue | null>(null)

export const SiteEditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin } = useAuth()
  const { loading, saveContent, getValue } = useSiteContent()
  const [editMode, setEditMode] = useState(false)
  const [saving, setSaving] = useState(false)

  const get = useCallback(
    (key: string, fallback = '') => getValue(key, fallback),
    [getValue]
  )

  const save = useCallback(
    async (
      key: string,
      value: string,
      meta?: { type?: ContentType; section?: string; label?: string }
    ) => {
      setSaving(true)
      try {
        await saveContent(key, value, meta)
        toast.success('Modification enregistrée.')
      } catch (error) {
        console.error('Echec de l enregistrement:', error)
        toast.error('Échec de l\'enregistrement.')
        throw error
      } finally {
        setSaving(false)
      }
    },
    [saveContent]
  )

  return (
    <SiteEditContext.Provider
      value={{
        get,
        save,
        editMode,
        setEditMode,
        canEdit: !!isAdmin,
        saving,
        loading,
      }}
    >
      {children}
    </SiteEditContext.Provider>
  )
}

export function useSiteEdit(): SiteEditContextValue {
  const ctx = useContext(SiteEditContext)
  if (!ctx) {
    // Fallback neutre si le provider n'est pas monte (ex: pages isolees)
    return {
      get: (_key: string, fallback = '') => fallback,
      save: async () => {},
      editMode: false,
      setEditMode: () => {},
      canEdit: false,
      saving: false,
      loading: false,
    }
  }
  return ctx
}
